import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "./Seo";
import PageHero from "./UI/PageHero";
import SectionHeading from "./UI/SectionHeading";
import Breadcrumbs, { breadcrumbSchema } from "./UI/Breadcrumbs";
import CtaBand from "./UI/CtaBand";
import Icon from "./UI/Icon";
import { areas } from "../data/areas";
import { SITE } from "../data/site";

export default function AreasHub() {
  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Areas We Cover" },
  ];

  // Areas we serve that don't have a dedicated page yet.
  const otherAreas = SITE.areas.filter(
    (name) => name !== "Cambridge" && !areas.some((a) => a.name.startsWith(name))
  );

  return (
    <>
      <Seo
        title="Areas We Cover | Carpenter in Cambridge & Cambridgeshire | Cambridge Carpenter"
        description="Cambridge Carpenter covers Cambridge and the surrounding Cambridgeshire villages including Cherry Hinton, Trumpington, Histon, Milton, Girton and Waterbeach."
        canonical="https://www.cambridgecarpenter.co.uk/areas/"
        schema={breadcrumbSchema(crumbs)}
      />

      <PageHero
        eyebrow="Where we work"
        title="Areas we cover"
        intro="Based just north of Cambridge, we cover the city and the surrounding Cambridgeshire villages."
        crumbs={<Breadcrumbs items={crumbs} />}
      />

      <section className="max-w-site mx-auto px-5 sm:px-10 pt-20 pb-16 md:pt-28 md:pb-20">
        <div className="prose-links max-w-3xl">
          <SectionHeading
            eyebrow="Cambridge & Cambridgeshire"
            title="A local carpenter, close to home"
          />
          <p className="text-bark text-lg leading-8 mt-6">
            We're based in the villages just north of Cambridge, which keeps us close to most of the
            areas we serve and travel off your quote. Below are the areas with a dedicated page,
            plus the wider patch we cover. Don't see your village? <Link to="/contact">Get in touch</Link>{" "}
            and we'll let you know.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {areas.map((a) => (
            <Link
              key={a.slug}
              to={`/areas/${a.slug}`}
              className="group bg-linen border border-hairline p-6 hover:border-oak transition-colors flex items-start gap-4"
            >
              <span className="shrink-0 w-11 h-11 flex items-center justify-center bg-oak/10 text-oak">
                <Icon name="pin" className="w-6 h-6" />
              </span>
              <span>
                <span className="block font-display font-semibold text-xl text-ink group-hover:text-oak transition-colors">
                  {a.name}
                </span>
                <span className="block text-bark leading-6 mt-1 text-[15px]">{a.heroIntro}</span>
              </span>
            </Link>
          ))}
        </div>

        {otherAreas.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display font-semibold text-2xl text-ink mb-5">We also cover</h2>
            <ul className="flex flex-wrap gap-2.5">
              <li className="flex items-center gap-1.5 bg-linen border border-hairline px-3.5 py-2 text-sm text-bark">
                <Icon name="pin" className="w-4 h-4 text-oak" /> Cambridge
              </li>
              {otherAreas.map((name) => (
                <li key={name} className="flex items-center gap-1.5 bg-linen border border-hairline px-3.5 py-2 text-sm text-bark">
                  <Icon name="pin" className="w-4 h-4 text-oak" /> {name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <CtaBand />
    </>
  );
}
